import { getListDriversV3, type Driver } from '@/services/api/drivers';

/**
 * Rider-directory helpers shared by every 4W wizard (`CreateShipment4WModal`,
 * the UJP wizard). `GET /v3/drivers` carries the name under a few possible
 * keys and the phone under several — normalise once here.
 */

export const riderDisplayName = (d: Driver): string =>
  d.name || `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim();

export const riderPhoneOf = (d: Driver): string =>
  d.phone || d.phone_number || d.phoneNumber || d.mobile || '';

/**
 * The narrowing every 4W rider lookup applies. A 4W trip needs a truck DRIVER
 * (the directory also holds 2W RIDERs, warehouse staff and canvassers) who can
 * actually be assigned (ACTIVE / READY_TO_WORK — same set as the picker).
 */
export const DRIVER_4W_FILTER = {
  type: 'DRIVER',
  statuses: 'ACTIVE,READY_TO_WORK',
} as const;

/**
 * Resolve a driver NAME (a UJP row / CSV carries no id) to the one directory
 * record it unambiguously denotes — an exact name hit, or a single result.
 * Returns `null` on no match or an ambiguous one; the caller then leaves the
 * picker for a manual pick (names aren't unique and the source can't
 * disambiguate). Auto-selection is exactly the path nobody re-checks, so the
 * DRIVER + assignable narrowing is applied here too — never auto-assign a 2W
 * rider to a truck trip.
 */
export const findUniqueDriverByName = async (
  rawName: string
): Promise<Driver | null> => {
  const name = rawName.trim();
  if (!name) return null;
  const res = await getListDriversV3({
    page: '1',
    size: '10',
    search: name,
    ...DRIVER_4W_FILTER,
  });
  const list = res.data ?? [];
  const exact = list.filter(
    (d) => riderDisplayName(d).trim().toLowerCase() === name.toLowerCase()
  );
  return exact.length === 1 ? exact[0] : list.length === 1 ? list[0] : null;
};
